import { useState } from "react";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";
import { useAdminAnalytics, TimeframeOption } from "@/hooks/useAdminAnalytics";
import { useAdminUserActivity } from "@/hooks/useAdminUserActivity";
import { AnalyticsStatsCards } from "./analytics/AnalyticsStatsCards";
import { PageViewsChart } from "./analytics/PageViewsChart";
import { TopPagesChart } from "./analytics/TopPagesChart";
import { FeatureUsageChart } from "./analytics/FeatureUsageChart";
import { InteractionsTable } from "./analytics/InteractionsTable";
import { UserActivityTable } from "./analytics/UserActivityTable";

const TIMEFRAME_OPTIONS: { value: TimeframeOption; label: string }[] = [
  { value: "1d", label: "Last 24h" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "all", label: "All time" },
];

export const AnalyticsTab = () => {
  const [timeframe, setTimeframe] = useState<TimeframeOption>("7d");
  const {
    stats,
    isLoadingStats,
    pageViewsChart,
    isLoadingPageViewsChart,
    topPages,
    isLoadingTopPages,
    featureUsage,
    isLoadingFeatureUsage,
    recentInteractions,
    isLoadingRecentInteractions,
    refetch,
    lastFetchedAt,
    isRefreshing,
  } = useAdminAnalytics(timeframe);

  const { 
    data: userActivity, 
    isLoading: isLoadingUserActivity, 
    refetch: refetchUserActivity,
    isRefreshing: isRefreshingUserActivity,
  } = useAdminUserActivity(timeframe);

  const isAnyRefreshing = isRefreshing || isRefreshingUserActivity;

  const handleRefresh = () => {
    refetch();
    refetchUserActivity();
  };

  return (
    <div className="space-y-6">
      {/* Header with timeframe filter */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">User Analytics</h2>
          <p className="text-muted-foreground">
            Track user interactions, page views, and feature usage
          </p>
          {lastFetchedAt && (
            <p className="text-xs text-muted-foreground mt-1">
              Last updated {formatDistanceToNow(lastFetchedAt, { addSuffix: true })}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border p-1">
            {TIMEFRAME_OPTIONS.map((option) => (
              <Button
                key={option.value}
                variant={timeframe === option.value ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setTimeframe(option.value)}
              >
                {option.label}
              </Button>
            ))}
          </div>
          <Button 
            variant="outline" 
            size="icon" 
            onClick={handleRefresh}
            disabled={isAnyRefreshing}
          >
            <RefreshCw className={cn("h-4 w-4", isAnyRefreshing && "animate-spin")} />
          </Button>
        </div>
      </div>

      {/* Stats cards */}
      <AnalyticsStatsCards stats={stats} isLoading={isLoadingStats} />

      {/* User Activity Table - Prominent position */}
      <UserActivityTable data={userActivity} isLoading={isLoadingUserActivity} />

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PageViewsChart data={pageViewsChart} isLoading={isLoadingPageViewsChart} />
        <TopPagesChart data={topPages} isLoading={isLoadingTopPages} />
      </div>

      {/* Feature usage chart */}
      <FeatureUsageChart data={featureUsage} isLoading={isLoadingFeatureUsage} />

      {/* Recent interactions table */}
      <InteractionsTable data={recentInteractions} isLoading={isLoadingRecentInteractions} />
    </div>
  );
};
